import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import InlineDropdown from 'components/molecules/InlineDropdown';
import { translate } from 'utils/translate';

import Toggle from 'components/utilities/Toggle';
import Heading, { H1 } from 'components/atoms/Heading';
import { minus, plus } from 'components/atoms/Icon/icons';
import Button from 'components/atoms/Button';
import Icon from 'components/atoms/Icon';
import * as styles from './ProductFilter.scss';
import FilterItem from './FilterItem';

const TRANSLATED_NAMES = {
  AreaIds: '/pages/eventListPage/area',
  AwardIds: '/product/ecoCertyficate',
  CategoryIds: '/pages/productListPage/category',
  EventCategoryIds: '/pages/eventListPage/category',
};

const FilterGroup = ({
  group,
  open,
  onToggle,
  filterPage,
  filterGroupChar,
  compact,
  url,
  className,
  index,
  filtersActive,
  handleSetFilter,
}) => {
  const eventListView = filterPage === 'Event';
  const hideIndex = 10;
  const filterTitle = TRANSLATED_NAMES[filterPage + group.filterType]
    ? translate(TRANSLATED_NAMES[filterPage + group.filterType])
    : translate(TRANSLATED_NAMES[group.filterType]);
  // eslint-disable-next-line react/prop-types
  const isOrFilter = group.filterType.startsWith('Or');
  const allFilters = isOrFilter
    ? Object.values(group.filters).map(f => ({
        ...f,
        id: filterGroupChar + f.id,
      }))
    : Object.values(group.filters).map(f => ({
        ...f,
        id: f.id.toString(),
      }));
  const queryArray = (url.query[group.filterType] || '')
    .split(',')
    .filter(item => item !== '');

  const getIsFiltered = filter => {
    let isFiltered = false;
    if (Object.prototype.hasOwnProperty.call(filter, 'tellusCategoryIds')) {
      // Some filters contains the same tellusCategoryIds, and were therefore falsely set as isFiltered.
      // Set specifically to the eventListView to prevent possibly breaking something elsewhere.
      if (eventListView) {
        if (filter.tellusCategoryIds.every(id => queryArray.includes(id)))
          isFiltered = true;
      } else {
        queryArray.forEach(q => {
          if (filter.tellusCategoryIds.includes(q)) isFiltered = true;
        });
      }
    } else {
      isFiltered = queryArray.includes(filter.id);
    }
    return isFiltered;
  };

  const getNewQueryArray = (filter, isFiltered) => {
    if (Object.prototype.hasOwnProperty.call(filter, 'tellusCategoryIds')) {
      // Some filters contains the same tellusCategoryIds, and the existing setting of the queryArray
      // resulted in removing all these filters from the query when deselecting one of them.
      // Set specifically to the eventListView to prevent possibly breaking something elsewhere.

      const newQuery = [...queryArray]
        .toString()
        .replace(filter.tellusCategoryIds.toString(), '')
        .split(',')
        .filter(item => item !== '');
      return isFiltered
        ? newQuery
        : [...queryArray, ...filter.tellusCategoryIds];
    }
    return isFiltered
      ? queryArray.filter(id => id !== filter.id)
      : [...queryArray, filter.id];
  };

  if (group.dropdown === true) {
    return (
      <React.Fragment key={`${group.filterType}_${index}`}>
        <InlineDropdown
          label={filterTitle}
          open={open}
          onChange={onToggle}
          toggleable={group.toggleable}
          className={classnames(styles.root, styles.dropdown, className)}
        >
          <ul className={styles.list}>
            {allFilters.map(filter => {
              const isFiltered = getIsFiltered(filter);
              const newQueryArray = getNewQueryArray(filter, isFiltered);
              return (
                <li className={styles.item} key={filter.id}>
                  <FilterItem
                    key={`${group.filterType}_${filter.id}`}
                    filter={filter}
                    type={group.filterType}
                    active={isFiltered}
                    filterQuery={newQueryArray}
                    compact={compact}
                    url={url}
                    filtersActive={filtersActive}
                    handleSetFilter={handleSetFilter}
                  />
                </li>
              );
            })}
          </ul>
        </InlineDropdown>
      </React.Fragment>
    );
  }

  return (
    <section
      key={`${group.filterType}_${index}`}
      className={classnames(styles.root, className)}
    >
      <H1 size={Heading.sizes.heading4} className={styles.heading}>
        {filterTitle}
      </H1>
      {!group.toggleable ? (
        <ul className={styles.list}>
          {allFilters.map(filter => {
            const isFiltered = getIsFiltered(filter);
            const newQueryArray = getNewQueryArray(filter, isFiltered);

            return (
              <li className={styles.item} key={filter.id}>
                <FilterItem
                  key={`${group.filterType}_${filter.id}`}
                  filter={filter}
                  type={group.filterType}
                  active={isFiltered}
                  filterQuery={newQueryArray}
                  compact={compact}
                  url={url}
                  filtersActive={filtersActive}
                  handleSetFilter={handleSetFilter}
                />
              </li>
            );
          })}
        </ul>
      ) : (
        <ul className={styles.list}>
          {allFilters.map((filter, i) => {
            if (i < hideIndex) {
              const isFiltered = getIsFiltered(filter);
              const newQueryArray = getNewQueryArray(filter, isFiltered);
              return (
                <li className={styles.item} key={filter.id}>
                  <FilterItem
                    key={`${group.filterType}_${filter.id}`}
                    filter={filter}
                    type={group.filterType}
                    active={isFiltered}
                    filterQuery={newQueryArray}
                    compact={compact}
                    url={url}
                    filtersActive={filtersActive}
                    handleSetFilter={handleSetFilter}
                  />
                </li>
              );
            }
            return null;
          })}

          <Toggle
            on={false}
            render={({ on, toggle }) => (
              <React.Fragment>
                {on &&
                  Object.values(group.filters).map((filter, i) => {
                    if (i >= hideIndex) {
                      const isFiltered = getIsFiltered(filter);

                      const newQueryArray = getNewQueryArray(
                        filter,
                        isFiltered,
                      );

                      return (
                        <li className={styles.item} key={filter.id}>
                          <FilterItem
                            key={`${group.filterType}_${filter.id}`}
                            filter={filter}
                            type={group.filterType}
                            active={isFiltered}
                            filterQuery={newQueryArray}
                            compact={compact}
                            url={url}
                            filtersActive={filtersActive}
                            handleSetFilter={handleSetFilter}
                          />
                        </li>
                      );
                    }
                    return null;
                  })}

                <li>
                  <Button
                    transparent
                    onClick={toggle}
                    className={styles.toggle}
                    noPadding
                  >
                    <Icon icon={on ? minus : plus} />
                    {on
                      ? translate('/pages/productListPage/showLess')
                      : translate('/pages/productListPage/showMore')}
                  </Button>
                </li>
              </React.Fragment>
            )}
          />
        </ul>
      )}
    </section>
  );
};

FilterGroup.propTypes = {
  onToggle: PropTypes.func,
  filterPage: PropTypes.string,
  filterGroupChar: PropTypes.string,
  url: PropTypes.object,
  open: PropTypes.bool,
  compact: PropTypes.bool,
  className: PropTypes.string,
  index: PropTypes.number,
  handleSetFilter: PropTypes.func,
  filtersActive: PropTypes.object,
  group: PropTypes.shape({
    filterType: PropTypes.string,
    filters: PropTypes.any,
    toggleable: PropTypes.bool,
    dropdown: PropTypes.bool,
  }),
};

export default FilterGroup;
